/**
 * Tipos compartilhados do dashboard customizavel por usuario.
 *
 * Cada usuario pode reordenar/redimensionar os widgets de algumas
 * paginas (home, clients). O layout vive na tabela
 * `user_dashboard_layouts` (jsonb), uma linha por (user, page_id).
 *
 * Grid: 4 colunas x 3 linhas (12 slots logicos). Cada widget ocupa um
 * retangulo `(x, y, w, h)` com w em 1..4 e h em 1..3. A ordem dos itens
 * no array nao importa para layout — `x`/`y` mandam.
 */

/**
 * IDs das paginas que tem layout customizavel. Centralizado para validar
 * `pageId` recebido nas rotas `GET/PUT /me/dashboard-layout/:pageId`.
 */
export const DASHBOARD_PAGE_IDS = ['home', 'clients', 'financial', 'atividades'] as const;
export type DashboardPageId = (typeof DASHBOARD_PAGE_IDS)[number];

/**
 * Widgets da pagina 'home' (dashboard principal do escritorio).
 */
export const WIDGET_IDS = [
  'clients',
  'processes',
  'recent_clients',
  'upcoming_deadlines',
  'financial',
  'pending_tasks',
  'today_agenda',
  'new_intimations',
  'agenda_month',
] as const;
export type WidgetId = (typeof WIDGET_IDS)[number];

/**
 * Widgets da pagina 'clients' (dashboard do modulo D01).
 *
 * Refletem os 7 cards definidos na tela de clientes:
 *  - summary_total            — total de contatos
 *  - summary_with_process     — clientes com processo ativo
 *  - summary_without_process  — clientes sem processo ativo
 *  - top_sources              — top 5 origens de captacao
 *  - age_ranges               — distribuicao por faixa etaria
 *  - top_professions          — top 5 profissoes
 *  - birthdays                — aniversariantes do mes
 */
export const CLIENTS_WIDGET_IDS = [
  'summary_total',
  'summary_with_process',
  'summary_without_process',
  'top_sources',
  'age_ranges',
  'top_professions',
  'birthdays',
] as const;
export type ClientsWidgetId = (typeof CLIENTS_WIDGET_IDS)[number];

/**
 * Widgets da pagina 'financial' (dashboard do modulo D09).
 *
 *  - summary_saldo                  — saldo total atual + variacao % mes anterior
 *  - summary_receita_prevista       — receita prevista vs realizada do mes
 *  - summary_despesa_prevista       — despesa prevista vs realizada do mes
 *  - summary_pagamentos_atrasados   — total e count de atrasados, CTA "Mostrar"
 *  - chart_receita_despesa          — bar chart 6 meses receita vs despesa
 *  - contas_bancarias               — lista compacta de contas com saldo
 *  - top_categorias_receita         — top 5 categorias de receita do mes
 *  - top_categorias_despesa         — top 5 categorias de despesa do mes
 *  - recent_transactions            — ultimas 5 transactions pagas
 */
export const FINANCIAL_WIDGET_IDS = [
  'summary_saldo',
  'summary_receita_prevista',
  'summary_despesa_prevista',
  'summary_pagamentos_atrasados',
  'chart_receita_despesa',
  'contas_bancarias',
  'top_categorias_receita',
  'top_categorias_despesa',
  'recent_transactions',
] as const;
export type FinancialWidgetId = (typeof FINANCIAL_WIDGET_IDS)[number];

/**
 * Widgets da pagina 'atividades' (dashboard do modulo D03).
 *
 *  - tasks_activity_chart  — bar chart pendentes/atrasadas/resolvidas no mes
 *  - tasks_productivity    — big number tarefas pendentes
 *  - tasks_calendar_count  — calendario do mes com badges de contagem por dia
 */
export const ATIVIDADES_WIDGET_IDS = [
  'tasks_activity_chart',
  'tasks_productivity',
  'tasks_calendar_count',
] as const;
export type AtividadesWidgetId = (typeof ATIVIDADES_WIDGET_IDS)[number];

/**
 * Allowlist de widget IDs por pagina. Validador de layout consulta
 * aqui para rejeitar IDs estrangeiros (ex: 'birthdays' em layout da home).
 */
export const WIDGETS_BY_PAGE: Record<DashboardPageId, readonly string[]> = {
  home: WIDGET_IDS,
  clients: CLIENTS_WIDGET_IDS,
  financial: FINANCIAL_WIDGET_IDS,
  atividades: ATIVIDADES_WIDGET_IDS,
};

/**
 * Tipo dinamico do widgetId baseado no pageId. Para o backend,
 * trabalhamos com `string` no shape generico e validamos com
 * `WIDGETS_BY_PAGE[pageId]`.
 */
export interface WidgetLayout {
  widgetId: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export const GRID_COLS = 4;
export const GRID_ROWS = 3;
export const WIDGET_MIN_W = 1;
export const WIDGET_MAX_W = 4;
export const WIDGET_MIN_H = 1;
export const WIDGET_MAX_H = 3;

export function isDashboardPageId(value: unknown): value is DashboardPageId {
  return (
    typeof value === 'string' &&
    (DASHBOARD_PAGE_IDS as readonly string[]).includes(value)
  );
}

/**
 * Valida que nenhum widget se sobrepoe a outro no grid. Util tanto para
 * o backend (rejeitar PUT invalido) quanto para o frontend (sanity check
 * antes de salvar). Retorna null se ok, ou uma mensagem de erro com o
 * par de widgets em conflito.
 */
export function validateNoOverlap(layout: WidgetLayout[]): string | null {
  for (let i = 0; i < layout.length; i++) {
    const a = layout[i];
    if (!a) continue;
    for (let j = i + 1; j < layout.length; j++) {
      const b = layout[j];
      if (!b) continue;
      const overlapX = a.x < b.x + b.w && b.x < a.x + a.w;
      const overlapY = a.y < b.y + b.h && b.y < a.y + a.h;
      if (overlapX && overlapY) {
        return `Widgets sobrepostos: ${a.widgetId} e ${b.widgetId}`;
      }
    }
  }
  return null;
}
